import Vue from 'vue';
import Component from 'vue-class-component';
import { namespace } from 'vuex-class';
import './login.component.scss';
import { ValidationProvider } from 'vee-validate';
import { v4 as uuidv4 } from 'uuid';
import { SettingStoreConst } from '@/common/store/setting.store';
import { LoginRequest } from '@/common/model/request/login-request.model';
import DeviceService from '../../service/device.service';
import FlashMessage from '@/common/components/flash-message.component';
import AlertMessageComponent from '@/common/components/alert-message/alert-message.component';
import { MstDeviceType } from '@/common/enum/mst-device-type.enum';
import SettingService from '@/common/service/setting.service';
import AppConstants from '@/common/constants/app.constants';
import { UserLastAccessInfo } from '@/common/model/user-last-access-info.model';
import store from '@/common/store/index';
import SortingService from '@/tsort/service/sorting.service';
import ErrorCdMsgConstants from '@/common/constants/error-cd-msg.constants';
import { SortStoreConst } from '@/tsort/store/sort.store';
import { ScanSettingResponse } from '@/tsort/model/response/scan-setting-response.model';
import RouterUtils from '@/common/utils/router.util';
import InductionService from '@/common/service/induction.service';
const SettingStore = namespace(SettingStoreConst.SETTING_STORE_MODULE_NAME);
const SortStore = namespace(SortStoreConst.SORT_STORE_MODULE_NAME);
const Auth = namespace('Auth');
@Component({})
export default class LoginComponent extends Vue {
  isShowModelRequest = false;
  isNewDevice = false;
  deviceService = new DeviceService();
  settingService = new SettingService();
  sortService = new SortingService();
  inductionService = new InductionService();
  // Array of pages that do not check link induction
  allowablePages = [AppConstants.NAME_HAND_SORTING_PAGE, AppConstants.NAME_SETTING_PAGE, AppConstants.NAME_TOP_PAGE];

  @Auth.Getter
  private isLoggedIn!: boolean;

  @Auth.Action
  private login!: (loginRequest: LoginRequest) => Promise<string | boolean>;

  @Auth.Action
  private logout!: () => void;

  @SettingStore.Action
  updateDeviceId!: (value: string) => void;

  @SettingStore.Action
  updateInductionId!: (value: string) => void;

  @SettingStore.Action
  updateInductionName!: (value: string) => void;

  @SettingStore.Getter
  deviceType!: MstDeviceType;

  @SettingStore.Getter
  externalDeviceId!: string;

  @SettingStore.Action
  updateExternalDeviceId!: (value: string) => void;

  @SettingStore.Action
  updateRestoreScreen!: (value: UserLastAccessInfo) => void;

  @SettingStore.Action
  updateInductionBreakingFlag!: (value: boolean) => void;

  private loginRequest: LoginRequest = { deviceId: '', employeeCode: '' };
  @SettingStore.Getter
  deviceId!: string;

  @SettingStore.Getter
  inductionId!: string;

  @SettingStore.Getter
  restoreScreen!: string;

  @Auth.Getter
  userId!: number;

  @SortStore.Action
  updateScanSettingOnLogin!: (value: ScanSettingResponse) => void;

  @SortStore.Getter
  scanSetting!: ScanSettingResponse;

  private loginErrorMsg: string | null = '';
  private errors: string[] = [];
  private isOnFieldClick = false;
  private uuid = '';

  mounted(): void {
    // Already logged-in => redirect to before page
    if (this.isLoggedIn) {
      this.$router.push({ name: this.restoreScreen || AppConstants.NAME_TOP_PAGE });
    }
  }

  closeModelRequest() {
    this.isShowModelRequest = false;
    return;
  }

  async requestApproveNewDevice() {
    const dataResponse = await this.deviceService.deviceRegister(this.externalDeviceId);
    this.isShowModelRequest = false;
    if (dataResponse.code === 200) {
      FlashMessage.getInstance().showSuccessMessage('承認リクエストを送りました。');
      this.loginRequest.deviceId = this.externalDeviceId;
    } else if (dataResponse.code === 500) {
      FlashMessage.getInstance().showErrorMessage(dataResponse.msg);
    }
  }

  private async onClickLogin(): Promise<void> {
    const resultValid = await (this.$refs.validationProvider as InstanceType<typeof ValidationProvider>).validate();
    if (!resultValid.valid) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    const deviceRegistrationMode = config && config['DEVICE_REGISTRATION_MODE'];
    // DEVICE_REGISTRATION_MODE = 1 => register device
    if (deviceRegistrationMode) {
      //  Check to generate External Device Id
      if (!this.externalDeviceId) {
        this.uuid = `Ta${uuidv4()}`;
        this.updateExternalDeviceId(this.uuid);
        this.isNewDevice = true;
      } else {
        this.uuid = this.externalDeviceId;
        await this.checkDeviceExistence();
      }

      // check to show modal request registration device
      if (this.isNewDevice) {
        this.isShowModelRequest = true;
        if (store.getters.getNetworkConnectionMode) {
          FlashMessage.getInstance().hideAllMessages();
        }
      } else {
        await this.checkLogin();
      }
    } else {
      // DEVICE_REGISTRATION_MODE = 0 => skip register device
      // Check to generate External Device Id
      if (!this.externalDeviceId) {
        this.updateExternalDeviceId(`Ta${uuidv4()}`);
      }
      this.loginRequest.deviceId = '';
      await this.checkLogin();
    }
  }

  async checkDeviceExistence() {
    let externalDeviceIdExistence = false;
    this.isNewDevice = false;
    const dataResponse = await this.deviceService.checkDeviceExistence(this.externalDeviceId, this.deviceType);
    externalDeviceIdExistence = dataResponse?.result;
    if (!externalDeviceIdExistence) {
      this.isNewDevice = true;
    } else {
      this.loginRequest.deviceId = this.externalDeviceId;
      this.updateDeviceId(dataResponse?.deviceId);
    }
  }

  async checkLogin() {
    // Call login API
    const loggedIn = await this.login(this.loginRequest).catch((error) => {
      // Display error message
      this.loginErrorMsg = error;
    });
    if (loggedIn) {
      const isRestoreScreenNotRequiredInduction = this.allowablePages.includes(this.restoreScreen);
      const userLastAccessInfo = {
        userId: this.userId,
        externalDeviceId: this.externalDeviceId,
        inductionId: '',
        // if user last access
        lastAccessedScreen: isRestoreScreenNotRequiredInduction ? this.restoreScreen : AppConstants.NAME_TOP_PAGE
      } as UserLastAccessInfo;
      try {
        const scanSettingRes = await this.sortService.getScanSetting();
        this.updateScanSettingOnLogin(scanSettingRes);
        const dataResponse = await this.settingService.getLinkInduction(
          this.externalDeviceId,
          AppConstants.DEVICE_TYPE
        );
        this.updateDeviceId('');
        this.updateInductionBreakingFlag(false);
        if (dataResponse && dataResponse.mstInduction) {
          const inductionInfo = dataResponse.mstInduction;
          this.updateInductionId(inductionInfo.inductionId);
          this.updateInductionName(inductionInfo.inductionName);
          this.updateDeviceId(inductionInfo.deviceId);
          // インダクションの状態を確認する
          const isRedirectToBreakInductionPage = await this.inductionService.isRedirectToBreakInductionPage(
            scanSettingRes?.inductionProcessingMode,
            inductionInfo.inductionId
          );
          if (isRedirectToBreakInductionPage) {
            return;
          }
        } else {
          this.updateInductionId('');
          this.updateInductionName('');
          this.updateRestoreScreen(userLastAccessInfo);
        }
      } catch (error) {
        this.updateDeviceId('');
        this.updateInductionId('');
        this.updateInductionName('');
        this.updateRestoreScreen(userLastAccessInfo);
      }
      if (!this.deviceId) {
        // デバイスを作成する
        const dataResponse = await this.deviceService.createDevice(this.externalDeviceId).catch(() => {
          this.logout();
          return;
        });
        if (!dataResponse) return;
        this.updateDeviceId(dataResponse.mstDevice?.deviceId);
      }

      // インダクションIDが空で、inductionProcessingModeがtrueの場合、設定ページにリダイレクトする
      if (!isRestoreScreenNotRequiredInduction && !this.inductionId && this.scanSetting?.inductionProcessingMode) {
        await RouterUtils.goToRouterName(AppConstants.NAME_SETTING_PAGE);
        FlashMessage.getInstance().showErrorMsgWithErrorCode(
          ErrorCdMsgConstants.FUNC_WITHOUT_SELECTING_INDUCTION_ON_REQUIRED_SCREEN_ERR_CD,
          ErrorCdMsgConstants.FUNC_WITHOUT_SELECTING_INDUCTION_ON_REQUIRED_SCREEN_ERR_MSG
        );
        return;
      }

      // Authenticate successfully => redirect to before page
      await RouterUtils.goToRouterName(this.restoreScreen || AppConstants.NAME_TOP_PAGE);
    }
  }

  private async onInputEmployeeCode(event: KeyboardEvent) {
    if (event.key == 'Enter') {
      (this.$refs['loginId'] as HTMLInputElement)?.blur();
      (this.$refs['loginId'] as HTMLInputElement)?.focus();
      if (this.errors.length === 0 && this.loginRequest.employeeCode.length !== 0) {
        this.onClickLogin();
      }
    }
    this.resetLoginErrorMsg();
  }

  private onFieldsClickEvent(): void {
    if (!this.isOnFieldClick) {
      (this.$refs[`loginId`] as HTMLInputElement)?.select();
      this.isOnFieldClick = true;
    }
  }

  private resetLoginErrorMsg(): void {
    this.loginErrorMsg = null;
  }

  render() {
    return (
      <div class="container-fluid h-100 login-page">
        <AlertMessageComponent />
        <div class="d-flex row justify-content-center align-items-center login-container">
          <div id="login">
            <h1 class="icon-hub_logo_yoko_black_background mx-auto"></h1>
            <h6 class="login-title">スキャン ログイン</h6>
            <b-container class="pl-0 mb-2">
              {this.loginErrorMsg && <div class="text-danger mb-10">{this.loginErrorMsg}</div>}
            </b-container>
            {/* ID */}
            <ValidationProvider
              name="社員ID"
              ref="validationProvider"
              rules={{ required: true }}
              mode="aggressive"
              scopedSlots={{
                default: ({ errors, dirty }: { errors: string[]; dirty: boolean }) => {
                  if (errors && dirty) {
                    this.errors = errors;
                  }
                  return (
                    <div class="form-group mb-0">
                      <b-form-input
                        type="text"
                        ref="loginId"
                        autofocus
                        placeholder="社員ID"
                        vModel={this.loginRequest.employeeCode}
                        vOn:keyup_prevent={(event: KeyboardEvent) => this.onInputEmployeeCode(event)}
                        class={{
                          'rounded-0 border border-2 form-control': true,
                          'border-danger bg-danger-outline': this.errors?.[0]
                        }}
                        onClick={() => this.onFieldsClickEvent()}
                        onBlur={() => (this.isOnFieldClick = false)}
                      ></b-form-input>
                      <span class="text-danger">{errors[0] ? '社員IDは必須です。' : null}&nbsp;</span>
                    </div>
                  );
                }
              }}
            ></ValidationProvider>
            {/* Login button  */}
            <div class="btn-login mt-0">
              <button
                onClick={() => {
                  this.onClickLogin();
                }}
              >
                ログイン
              </button>
            </div>
          </div>
        </div>
        <b-modal
          id="normal-sort-modal"
          v-model={this.isShowModelRequest}
          centered
          size="md"
          no-close-on-backdrop={true}
          hide-footer
          hide-header
        >
          <h4 class="text-center font-weight-bold pb-4 pt-3">登録されていない端末です</h4>
          <p class="text-center gray-small-text">管理者に承認リクエストをしてください。</p>
          <p class="text-center gray-small-text font-weight-bold">{this.uuid}</p>
          <b-container fluid class="pt-5">
            <b-row class="h-100">
              <b-col cols="6" class="d-flex justify-content-center">
                <span
                  class="cursor-pointer"
                  onClick={() => {
                    this.closeModelRequest();
                  }}
                >
                  閉じる
                </span>
              </b-col>
              <b-col cols="6" class="d-flex justify-content-center">
                <span
                  class="font-weight-bold cursor-pointer"
                  onClick={() => {
                    this.requestApproveNewDevice();
                  }}
                >
                  承認リクエストする
                </span>
              </b-col>
            </b-row>
          </b-container>
        </b-modal>
      </div>
    );
  }
}
